import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { loginRequest } from "../config/authConfig";
import { msalInstance } from "../config/msalInstance";
import SessionExpiredModal from "../components/session/SessionExpiredModal";

/** Segundos de la cuenta regresiva antes de cerrar la sesion */
const COUNTDOWN_SECONDS: number = 59;

/** Cada cuanto se renueva el token de forma proactiva */
const TOKEN_REFRESH_MS: number = 200000;

/** Inactividad tolerada antes de preguntar si se sigue conectado */
const SESSION_TIMEOUT_MS: number = 500000;

/**
 * Vigia de sesion. Portado del proyecto `frontend` conservando el fix de
 * refresco de token documentado en TOKEN_REFRESH_SOLUTION_SUMMARY.md:
 *
 * 1. El refresco de token vive en su propio `useEffect` sin dependencias, por
 *    lo que sigue corriendo aunque el usuario cambie de pestania (`blur`).
 * 2. Un listener de `visibilitychange` revalida el token al volver a la
 *    pestania, antes de que el usuario pueda accionar nada.
 * 3. El timeout de inactividad solo programa el cierre de sesion, y si la
 *    sesion resulta estar sana se resetea en silencio.
 */
export default function SessionExpired() {
  const { instance } = useMsal();
  const [mouseStop, setMouseStop] = useState<boolean>(false);
  const [isOpenModalSessionTimeOut, setIsOpenModalSessionTimeOut] =
    useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(COUNTDOWN_SECONDS);

  // se escuchan los eventos de foco de la ventana
  useEffect(() => {
    const handleEventBlur = () => setMouseStop(true);
    const handleEventFocus = () => setMouseStop(false);

    window.addEventListener("blur", handleEventBlur);
    window.addEventListener("focus", handleEventFocus);

    return () => {
      window.removeEventListener("blur", handleEventBlur);
      window.removeEventListener("focus", handleEventFocus);
    };
  }, []);

  // cualquier interaccion del usuario reinicia el contador de inactividad
  useEffect(() => {
    const handleInteraction = () => setMouseStop(false);

    window.addEventListener("mousemove", handleInteraction);
    window.addEventListener("keypress", handleInteraction);

    return () => {
      window.removeEventListener("mousemove", handleInteraction);
      window.removeEventListener("keypress", handleInteraction);
    };
  }, []);

  // refresco de token: siempre activo, independiente de mouseStop
  useEffect(() => {
    const intervalToken = setInterval(() => {
      refreshToken();
    }, TOKEN_REFRESH_MS);

    return () => clearInterval(intervalToken);
  }, []);

  // listener de visibilitychange: revalida el token al volver a la pestania
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      const valid = await isSessionStillValid();
      if (!valid) setIsOpenModalSessionTimeOut(true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // timeout de inactividad: solo depende de mouseStop
  useEffect(() => {
    if (!mouseStop) return;

    const sessionTimeOut = setTimeout(async () => {
      const valid = await isSessionStillValid();

      if (!valid) {
        setIsOpenModalSessionTimeOut(true);
        return;
      }

      // sesion sana: se resetea el estado de inactividad en silencio
      setMouseStop(false);
      setCountdown(COUNTDOWN_SECONDS);
    }, SESSION_TIMEOUT_MS);

    return () => clearTimeout(sessionTimeOut);
  }, [mouseStop]);

  /**
   * Verifica si la sesion sigue siendo valida silenciosamente.
   * true  -> MSAL refresco/devolvio token sin interaccion, la sesion esta sana
   * false -> MSAL lanzo InteractionRequiredAuthError, se necesita re-login
   */
  const isSessionStillValid = async (): Promise<boolean> => {
    const account = msalInstance.getActiveAccount();
    if (!account) return false;

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: false,
      });

      if (response?.idToken) {
        sessionStorage.setItem("token", response.idToken);
      }

      return true;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) return false;
      // errores transitorios (red, race condition de MSAL): no molestar al usuario
      return true;
    }
  };

  const refreshToken = async (): Promise<void> => {
    const currentAccount = instance.getActiveAccount();
    if (!currentAccount) return;

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: currentAccount,
        forceRefresh: true,
        cacheLookupPolicy: CacheLookupPolicy.Default,
      });

      sessionStorage.setItem("token", response.idToken);
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) return;

      const request = { ...loginRequest, loginHint: currentAccount.username };

      try {
        const popupResponse = await msalInstance.acquireTokenPopup(request);

        if (popupResponse?.idToken) {
          sessionStorage.setItem("token", popupResponse.idToken);
        }
      } catch (popupError) {
        if (popupError instanceof InteractionRequiredAuthError) {
          await msalInstance.acquireTokenRedirect(request);
        }
      }
    }
  };

  const handleLogout = (): void => {
    sessionStorage.clear();
    localStorage.clear();
    setIsOpenModalSessionTimeOut(false);
    msalInstance.logoutRedirect();
  };

  const handleExtendSession = (): void => {
    setIsOpenModalSessionTimeOut(false);
    setCountdown(COUNTDOWN_SECONDS);
    refreshToken();
  };

  if (!isOpenModalSessionTimeOut) return null;

  if (countdown === 0) {
    return (
      <SessionExpiredModal
        title="Su sesión expiró"
        message="Vuelve a iniciar sesión para continuar."
        onConfirm={handleLogout}
        confirmLabel="Ingresar de nuevo"
      />
    );
  }

  return (
    <SessionExpiredModal
      title="Su sesión está a punto de expirar"
      subtitle="Por seguridad tu sesión se cerrará en:"
      message="Para mantenerse conectado, presione continuar."
      onCancel={handleLogout}
      onConfirm={handleExtendSession}
      confirmLabel="Continuar"
      cancelLabel="Cerrar sesión"
      countdown={countdown}
      setCountdown={setCountdown}
    />
  );
}
